import React, { useState, useEffect, useRef } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link } from "react-router-dom";
import { ChevronUp, PlusCircle } from "feather-icons-react/build/IconComponents";
import { setToogleHeader } from "../../core/redux/action";
import { useDispatch, useSelector } from "react-redux";
import AddPurchases from "../../core/modals/purchases/addpurchases";
import ImportPurchases from "../../core/modals/purchases/importpurchases";
import EditPurchases from "../../core/modals/purchases/editpurchases";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { useToast } from "../../Toaster/ToastContext";
import Loader from "../loader/loader";
import { apiClient } from "../../services/apiClient";
import { Table } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

const PurchasesList = () => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);
  const [purchases, setPurchases] = useState([]);
  const [filteredPurchases, setFilteredPurchases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPurchase, setCurrentPurchase] = useState(null);
  const { showToast } = useToast();
  const hasFetched = useRef(false);
  const [permissions, setPermissions] = useState({
    create: false,
    read: false,
    update: false,
    delete: false,
  });
  const user_type = localStorage.getItem("user_type");

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalPages: 0,
    totalCount: 0,
  });

  const renderCollapseTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Collapse
    </Tooltip>
  );

  const fetchPurchases = async (currentPage = 1, pageSize = 20) => {
    if (hasFetched.current) return;
    setLoading(true);
    const pageIndex = currentPage - 1;
    try {
      const response = await apiClient.post("/get-all-purchase", {
        pageIndex,
        pageSize,
        previousPageIndex: pageIndex,
      });
      if (response.success) {
        setPurchases(response.data);
        setFilteredPurchases(response.data);
        setPagination({
          currentPage: response.currentPage ?? currentPage,
          pageSize: response.pageSize ?? pageSize,
          totalPages: response.totalPages ?? 1,
          totalCount: response.totalCount || response.purchases.length,
        });
      } else {
        setPurchases([]);
        setFilteredPurchases([]);
      }
    } catch (error) {
      showToast(
        error.message || "Failed to fetch purchases",
        "error",
        "Purchases"
      );
    } finally {
      setLoading(false);
      hasFetched.current = true;
    }
  };

  const resetFetch = () => {
    hasFetched.current = false;
    fetchPurchases(pagination.currentPage, pagination.pageSize);
  };

  useEffect(() => {
    fetchPurchases(pagination.currentPage, pagination.pageSize);
  }, [pagination.currentPage, pagination.pageSize]);

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
    hasFetched.current = false;
  }, []);

  useEffect(() => {
    // fetchPurchases();
    const storedPermissions = localStorage.getItem("rolespermissions");
    try {
      if (!storedPermissions) return;

      const Permissions = JSON.parse(storedPermissions);
      const getPermissions = JSON.parse(Permissions.permissions);

      let parsedPermissions = getPermissions.permissions || getPermissions;

      const purchasesSection = parsedPermissions.find(
        (item) => item.title === "Purchases"
      );

      const purchasesPermissions = purchasesSection?.childPage?.find(
        (child) => child.title === "Purchases"
      );

      if (purchasesPermissions) {
        setPermissions({
          create: purchasesPermissions.create || false,
          read: purchasesPermissions.read || false,
          update: purchasesPermissions.update || false,
          delete: purchasesPermissions.delete || false,
        });
      } else {
        showToast(
          "Purchases permissions not found.",
          "error",
          "Permissions Error"
        );
      }
    } catch (error) {
      console.error("Error parsing permissions from localStorage:", error);
      showToast("Failed to load permissions.", "error", "Permissions Error");
    }
  }, []);

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();

    if (!term) {
      setFilteredPurchases(purchases);
      return;
    }

    setFilteredPurchases(
      purchases.filter(
        (purchase) =>
          (purchase.supplier_name && typeof purchase.supplier_name === "string"
            ? purchase.supplier_name.toLowerCase().includes(term)
            : false) ||
          (purchase.reference && typeof purchase.reference === "string"
            ? purchase.reference.toLowerCase().includes(term)
            : false)
      )
    );
  };

  const MySwal = withReactContent(Swal);

  const showConfirmationAlert = (purchase) => {
    MySwal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      confirmButtonText: "Yes, delete it!",
      cancelButtonColor: "#ff0000",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          await apiClient.post("/delete-purchase", {
            id: purchase.id,
          });
          showToast(
            "Purchase deleted successfully",
            "success",
            "Purchase Delete"
          );
          resetFetch();
        } catch (error) {
          showToast(
            error.message || "Failed to delete purchase",
            "error",
            "Purchase Delete"
          );
        } finally {
          setLoading(false);
        }
      } else {
        MySwal.close();
      }
    });
  };

  const showProductReturnAlert = (purchase) => {
    MySwal.fire({
      title: "Are you sure?",
      html: `You won't be able to revert this!<br>Returning order with reference: <strong>${purchase.reference}</strong>`,
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      confirmButtonText: "Yes, continue it!",
      cancelButtonColor: "#ff0000",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          await apiClient.post("/return-purchase", {
            id: purchase.id,
          });
          showToast(
            "Purchase returned successfully",
            "success",
            "Purchase Return"
          );
          resetFetch();
        } catch (error) {
          showToast(
            error.message || "Failed to return purchase",
            "error",
            "Purchase Return"
          );
        } finally {
          setLoading(false);
        }
      } else {
        MySwal.close();
      }
    });
  };

  const handleEditPurchase = async (purchase) => {
    try {
      const response = await apiClient.post("/edit-purchase", {
        id: purchase.id,
      });
      if (response.status) {
        setCurrentPurchase(response.data);
      }
    } catch (error) {
      showToast(
        error.message || "Failed to fetch purchase details",
        "error",
        "Purchase Edit"
      );
    }
  };
  const handleAddPurchase = () => {
    setCurrentPurchase(null);
  };

  const columns = [
    {
      title: "Supplier Name",
      dataIndex: "supplier_name",
      render: (text) => <span className="wrap-text">{text || "N/A"}</span>,
      sorter: (a, b) =>
        (a.supplier_name || "").length - (b.supplier_name || "").length,
      width: 200,
    },
    {
      title: "Reference",
      dataIndex: "reference",
      render: (text) => <span className="wrap-text">{text || "N/A"}</span>,
      sorter: (a, b) => (a.reference || "").length - (b.reference || "").length,
      width: 200,
    },
    {
      title: "Date",
      dataIndex: "date",
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
      render: (text) => {
        const date = new Date(text);
        return isNaN(date)
          ? "N/A"
          : date
              .toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
              .split("/")
              .join("-");
      },
      width: 150,
    },
    {
      title: "Status",
      dataIndex: "status",
      sorter: (a, b) => (a.status || "").length - (b.status || "").length,
      render: (text) => {
        const status = text ? text.toLowerCase() : "";
        if (status === "returned") {
          return <span className="badge bg-danger">Returned</span>;
        } else if (status === "pending") {
          return (
            <span className="badge" style={{ backgroundColor: "#06AED4" }}>
              Pending
            </span>
          );
        } else if (status === "received" || status === "recevied") {
          return <span className="badge bg-success">Received</span>;
        } else if (status === "ordered") {
          return <span className="badge bg-warning">Ordered</span>;
        }
        return text || "";
      },
      width: 150,
    },
    {
      title: "Total",
      dataIndex: "total",
      sorter: (a, b) => (a.total || 0) - (b.total || 0),
      render: (text) => <span className="wrap-text">{text || "0.00"}</span>,
      width: 150,
    },
    {
      title: "Paid",
      dataIndex: "paid",
      sorter: (a, b) => (a.paid || 0) - (b.paid || 0),
      render: (text) => <span className="wrap-text">{text || "0.00"}</span>,
      width: 150,
    },
    {
      title: "Due",
      dataIndex: "due",
      sorter: (a, b) => (a.due || 0) - (b.due || 0),
      render: (text) => <span className="wrap-text">{text || "0.00"}</span>,
      width: 150,
    },
    {
      title: "Payment Status",
      dataIndex: "payment_status",
      render: (text) => <span className="wrap-text">{text || "N/A"}</span>,
      width: 150,
      sorter: (a, b) =>
        (a.payment_status || "").length - (b.payment_status || "").length,
    },
    {
      title: () => (
        <div style={{ textAlign: 'center' }}>Action</div>
      ),
      dataIndex: "actions",
      key: "actions",
      render: (_, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            {/* Read (Edit Purchase) */}
            {permissions.read && (
              <Link
                className="me-2 p-2"
                data-bs-toggle="modal"
                data-bs-target="#add-units"
                onClick={() => handleEditPurchase(record)}
              >
                <i data-feather="edit" className="feather-edit" />
              </Link>
            )}

            {permissions.delete && (
              <Link
                className="confirm-text p-2"
                to="#"
                onClick={() => showConfirmationAlert(record)}
              >
                <i data-feather="trash-2" className="feather-trash-2" />
              </Link>
            )}

            {user_type === "business" && (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="return-tooltip">Return order</Tooltip>}
              >
                <Link
                  className="me-2 p-2 m-2"
                  to="#"
                  onClick={() => showProductReturnAlert(record)}
                >
                  <i data-feather="refresh-cw" className="feather-refresh-cw" />
                </Link>
              </OverlayTrigger>
            )}
          </div>
        </div>
      ),
      width: 200,
    },
  ];

  const { currentPage, pageSize, totalPages, totalCount } = pagination;
  const startItem = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = Math.min(startItem + pageSize - 1, totalCount);

  const pagesToShow = [];
  if (totalPages > 0) {
    if (totalPages <= 2) {
      for (let i = 1; i <= totalPages; i++) {
        pagesToShow.push(i);
      }
    } else {
      let middle = [];
      if (currentPage === 1) {
        middle = [1, 2];
      } else if (currentPage === totalPages) {
        middle = [totalPages - 1, totalPages];
      } else {
        middle = [currentPage, currentPage + 1];
      }

      if (middle[0] > 1) {
        pagesToShow.push(1);
      }
      if (middle[0] > 2) {
        pagesToShow.push("...");
      }
      middle.forEach((p) => pagesToShow.push(p));
      if (middle[1] < totalPages - 1) {
        pagesToShow.push("...");
      }
      if (middle[1] < totalPages) {
        pagesToShow.push(totalPages);
      }
    }
  }

  const changePage = (page) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
    }));
    hasFetched.current = false;
  };

  return (
    <div>
      <Loader loading={loading} />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header transfer">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Purchase List</h4>
                <h6>Manage your purchases</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <li>
                <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                  <Link
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    id="collapse-header"
                    className={data ? "active" : ""}
                    onClick={() => {
                      dispatch(setToogleHeader(!data));
                    }}
                  >
                    <ChevronUp />
                  </Link>
                </OverlayTrigger>
              </li>
            </ul>
            <div className="d-flex purchase-pg-btn">
              <div className="page-btn">
                {permissions.create && (
                  <Link
                    to="#"
                    className="btn btn-added"
                    data-bs-toggle="modal"
                    data-bs-target="#add-units"
                    onClick={handleAddPurchase}
                  >
                    <PlusCircle className="me-2" />
                    Add New Purchase
                  </Link>
                )}
              </div>
              {/* <div className="page-btn import">
                                <Link
                                    to="#"
                                    className="btn btn-added color"
                                    data-bs-toggle="modal"
                                    data-bs-target="#view-notes"
                                >
                                  
                                    <Download  className="me-2"/>
                                    Import Purchase
                                </Link>
                            </div> */}
            </div>
          </div>
          <div className="card table-list-card">
            <div className="card-body">
              <div className="table-top">
                <div className="search-set">
                  <div className="search-input">
                    <input
                      type="text"
                      placeholder="Search by supplier name and reference"
                      className="form-control form-control-sm formsearch"
                      onChange={handleSearch}
                    />
                    <Link to className="btn btn-searchset">
                      <i data-feather="search" className="feather-search" />
                    </Link>
                  </div>
                </div>
              </div>
              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={filteredPurchases}
                  rowKey={(record) => record.id || record.reference}
                  rowSelection={null}
                  pagination={false}
                />
              </div>

              {totalCount > 0 && (
                <div className="custom-pagination d-flex justify-content-between align-items-center mt-4 mb-4">
                  <div className="pagination-left">
                    Showing {startItem} - {endItem} of {totalCount}
                  </div>
                  <div className="pagination-right d-flex align-items-center">
                    <button
                      className="btn btn-sm btn-outline-secondary me-2"
                      disabled={currentPage === 1}
                      onClick={() => changePage(currentPage - 1)}
                    >
                      <LeftOutlined size={12} />
                    </button>
                    {pagesToShow.map((page, index) => (
                      <span key={index} className="me-2">
                        {page === "..." ? (
                          <span className="pagination-ellipsis">...</span>
                        ) : (
                          <button
                            className={`btn btn-sm ${
                              page === currentPage
                                ? "btn-primary"
                                : "btn-outline-secondary"
                            }`}
                            onClick={() => changePage(page)}
                          >
                            {page}
                          </button>
                        )}
                      </span>
                    ))}
                    <button
                      className="btn btn-sm btn-outline-secondary ms-2"
                      disabled={currentPage === totalPages}
                      onClick={() => changePage(currentPage + 1)}
                    >
                      <RightOutlined size={12} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <AddPurchases
        fetchPurchases={resetFetch}
        currentPurchase={currentPurchase}
        onClose={() => setCurrentPurchase(null)}
        onUpdate={resetFetch}
        canUpdate={permissions.update}
        canCreate={permissions.create}
      />
      <ImportPurchases />
      <EditPurchases />
    </div>
  );
};

export default PurchasesList;
